import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, styled, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { Link } from 'found';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CHECKOUT_PLANS } from '@klab-berlin/api-sdk/lib/constants/billing';
import { CheckoutPlan, PlanKey } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import { GetUpgradePreviewResponse } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import AppNavBar from '../../../components/AppLayout/MasterLayout/AppNavBar';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import {
  fetchMe,
  getStripeSubscriptionDetails,
  getUpgradePreview,
  upgradePlan,
} from '../../../actions/user.actions';
import {
  getStripeSubscriptionDetails as selectStripeSubscriptionDetails,
  selectUser,
} from '../../../reducers/user.selectors';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import { STRIPE_PLAN_DISPLAY } from '../../../constants/stripePlanBenefits';
import { routes } from '../../../constants';
import { getStripeUpgradeCheckoutUrl } from '../utils';

// PersonalDetailsSection.ACCOUNT; inlined — importing the enum drags the
// PersonalDetailsPage module chain and breaks Jest.
const ACCOUNT_SECTION = 'konto';

const AccountLink = styled(Link)({ color: palette.primary.main, textDecoration: 'none' });

// periodEnd is a date-only YYYY-MM-DD; format in UTC so it isn't shifted a day
// back in negative-offset timezones.
const formatPeriodEnd = (periodEnd: string): string =>
  new Date(periodEnd).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });

// SM-295: the left-column description promises the price change holds until the
// end of the current subscription term. mU subscriptions run a 12-month term, so
// the term end is the subscription start + 12 months. The upgrade preview carries
// no start/term field, so we derive it from the synced subscription's periodStart.
const SUBSCRIPTION_TERM_MONTHS = 12;
// periodStart is a full ISO timestamp. Derive the term end from its UTC
// calendar date (+12 months) and format in UTC, so the displayed day doesn't
// shift back in negative-offset timezones. Date.UTC normalizes the month
// overflow (month + 12) into the next year.
const formatTermEnd = (periodStart: string): string => {
  const start = new Date(periodStart);
  const termEnd = new Date(Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth() + SUBSCRIPTION_TERM_MONTHS,
    start.getUTCDate(),
  ));
  return termEnd.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });
};

const VALID_TARGET_PLANS: readonly CheckoutPlan[] = CHECKOUT_PLANS;
const isValidTargetPlanKey = (value: unknown): value is CheckoutPlan =>
  typeof value === 'string' && (VALID_TARGET_PLANS as readonly string[]).includes(value);

// Translation keys keyed by the kimuno-side `reason` discriminator (mirrors
// the list page's resolution map). Flat — we pick the right inline error copy
// without parsing the human message.
const REASON_TRANSLATION_KEY: Record<string, string> = {
  trialing: 'stripeUpgrade.error.trialing',
  past_due: 'stripeUpgrade.error.past_due',
  not_allowed: 'stripeUpgrade.error.not_allowed',
  invalid_status: 'stripeUpgrade.error.invalid_status',
  stripe_error: 'stripeUpgrade.error.stripe_error',
  idempotency_collision: 'stripeUpgrade.error.idempotency_collision',
  database_error: 'stripeUpgrade.error.database_error',
  catalog_missing: 'stripeUpgrade.error.catalog_missing',
  tax_config: 'stripeUpgrade.error.tax_config',
};

const resolveErrorTranslationKey = (error: unknown): string => {
  const e = error as { reason?: string; response?: { data?: { reason?: string } } } | null;
  const reason = e?.reason ?? e?.response?.data?.reason;
  if (reason && REASON_TRANSLATION_KEY[reason]) {
    return REASON_TRANSLATION_KEY[reason];
  }
  return 'stripeUpgrade.error.generic';
};

const SOFT_POLL_INTERVAL_MS = 1000;
const SOFT_POLL_MAX_DURATION_MS = 5000;
const SUCCESS_BANNER_LINGER_MS = 1500;

const formatEuro = (cents: number): string => {
  const euros = cents / 100;
  return `${euros.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const StripeUpgradeConfirmPage: React.FC = () => {
  const { t } = useTranslation();
  const { router, match } = useRouter();
  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const user = useSelector(selectUser);
  const stripeSubscriptionDetails = useSelector(selectStripeSubscriptionDetails);
  const fetchUpgradePreview = useAction(getUpgradePreview);
  const fetchStripeSubscriptionDetails = useAction(getStripeSubscriptionDetails);
  const submitUpgrade = useAction(upgradePlan);
  const refreshMe = useAction(fetchMe);

  const queryTargetPlan = (match.location.query as { targetPlanKey?: string })?.targetPlanKey;
  const targetPlanKey: CheckoutPlan | null = isValidTargetPlanKey(queryTargetPlan) ? queryTargetPlan : null;

  const [preview, setPreview] = useState<GetUpgradePreviewResponse | null>(null);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorKey, setSubmitErrorKey] = useState<string | null>(null);
  const [didSucceed, setDidSucceed] = useState(false);

  // Redirect non-eligible users away. The only allowed cohort is users with a
  // mapped `payment.stripeCustomerId` (incl. basis-with-stripe-customer
  // re-subscribers). Zuora-billed users and fresh Basis users (no Stripe
  // customer record) belong on the legacy `/premium` flow and get bounced
  // to the dashboard. Trialing users are excluded — the upgrade endpoint
  // rejects mid-trial changes; redirecting upfront avoids the dead-end.
  useEffect(() => {
    if (!user) return;
    if (!user.isStripeUser) {
      router.push(`/${routes.dashboard.root}`);
      return;
    }
    if (user.isInTrial) {
      router.push(`/${routes.dashboard.root}`);
      return;
    }
    // SM-202. Basis-with-Stripe users never belong on the confirm page —
    // they have no active subscription, so the preview proration is zero
    // and the saved-payment-method UI doesn't apply. The list page already
    // routes them around this page; this redirect is the safety net for
    // back-button / direct-URL navigation.
    if (user.isBasisUser && targetPlanKey) {
      router.push(getStripeUpgradeCheckoutUrl(targetPlanKey));
    }
  }, [user, router, targetPlanKey]);

  // No target plan in the query → bounce back to the list page.
  useEffect(() => {
    if (!targetPlanKey) {
      router.push(`/${routes.stripeUpgrade.root}`);
    }
  }, [targetPlanKey, router]);

  // SM-295: the left-column term-end date is derived from the synced
  // subscription's periodStart, which isn't part of the upgrade preview. Fetch
  // the details on mount so the date renders on direct navigation too; a stale
  // Redux value from a prior page still shows immediately. A failure is
  // non-fatal — the description falls back to the plain (no-date) copy.
  useEffect(() => {
    fetchStripeSubscriptionDetails().catch(() => {/* non-fatal */});
    // Mount-once — the wrapper is a fresh reference on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!targetPlanKey) return;
    let active = true;
    // Clear the previous plan's preview + any stale load error before the
    // new fetch starts — otherwise the breakdown box keeps rendering the
    // old plan's prices (and the CTA stays enabled against the old plan)
    // while the new request is in flight.
    setPreview(null);
    setLoadError(false);
    fetchUpgradePreview(targetPlanKey)
      .then((result) => {
        if (!active) return;
        setPreview(result);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
      });
    return () => {
      active = false;
    };
    // Mount-once — `fetchUpgradePreview` is a fresh wrapper from `useAction`
    // on every render. The effect re-runs when the targetPlanKey changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPlanKey]);

  const handleConfirm = async () => {
    if (!targetPlanKey || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitErrorKey(null);

    try {
      try {
        await submitUpgrade(targetPlanKey);
      } catch (error) {
        // Only `submitUpgrade()` itself failing should surface as an upgrade
        // error to the user. A failure inside the soft-poll below means the
        // backend mutation already succeeded — we don't want to re-enable the
        // CTA or show "upgrade failed" in that case.
        setSubmitErrorKey(resolveErrorTranslationKey(error));
        return;
      }

      // Soft-poll fetchMe() so the user lands on the dashboard once the
      // webhook entitlement mirror has updated their tier. Bound to ~5s so
      // we never block indefinitely. A transient refreshMe() error here
      // shouldn't kill the success path — the entitlement mirror is
      // eventually consistent and the dashboard will reconcile on its own.
      const startTime = Date.now();
      while (Date.now() - startTime < SOFT_POLL_MAX_DURATION_MS) {
        await new Promise((resolve) => setTimeout(resolve, SOFT_POLL_INTERVAL_MS));
        try {
          await refreshMe(true);
        } catch {
          // Stop polling on the first refresh failure and proceed to
          // success/navigation; the upgrade itself already succeeded.
          break;
        }
      }

      setDidSucceed(true);
      setTimeout(() => router.push(`/${routes.dashboard.root}`), SUCCESS_BANNER_LINGER_MS);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLeftColumn = () => {
    if (loadError) {
      return (
        <Alert severity='error' data-testid='stripe-upgrade-confirm-load-error'>
          {t('stripeUpgrade.loadFailed')}
        </Alert>
      );
    }

    if (!preview || !targetPlanKey) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spaces.md,
            paddingY: spaces.xxxl,
          }}
        >
          <CircularProgress
            data-testid='stripe-upgrade-confirm-loading'
            sx={{ '& .MuiCircularProgress-svg': { width: '100%', height: '100%' } }}
          />
        </Box>
      );
    }

    const planDisplay = STRIPE_PLAN_DISPLAY[targetPlanKey as PlanKey];
    const {
      currentMonthlyPrice,
      newMonthlyPrice,
      prorationAmount,
      cadence,
      currentPlanKey,
      discount,
    } = preview;
    const currentPlanDisplay = STRIPE_PLAN_DISPLAY[currentPlanKey as PlanKey];
    const cadenceLabel = cadence === 'yearly'
      ? t('stripeUpgrade.confirm.cadenceYearly')
      : t('stripeUpgrade.confirm.cadenceMonthly');
    // SM-295: the description promises the change holds until the end of the
    // current subscription term (start + 12 months), not the next monthly
    // billing date. Fall back to the plain copy when the subscription start is
    // not (yet) available.
    const periodStart = stripeSubscriptionDetails?.subscription?.periodStart;
    const formattedTermEnd = periodStart ? formatTermEnd(periodStart) : null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.lg,
          maxWidth: 480,
          marginX: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.sm,
            alignItems: 'flex-start',
          }}
          data-testid='stripe-upgrade-confirm-plan-transition'
        >
          <Chip
            data-testid='stripe-upgrade-confirm-current-plan-chip'
            label={t('stripeUpgrade.confirm.currentPlanLabel', {
              planName: t(currentPlanDisplay.packageName),
            })}
            size='small'
            sx={{
              backgroundColor: palette.action.hover,
              color: palette.text.secondary,
              fontWeight: 500,
            }}
          />
          <Typography
            variant='h4'
            color={palette.text.primary}
            sx={{ fontWeight: 600 }}
            data-testid='stripe-upgrade-confirm-plan-name'
          >
            {t(planDisplay.packageName)}
          </Typography>
        </Box>
        <Typography
          variant='body2'
          color={palette.text.secondary}
          data-testid='stripe-upgrade-confirm-description'
        >
          {formattedTermEnd
            ? t('stripeUpgrade.confirm.descriptionWithTermEnd', {
              planName: t(planDisplay.packageName),
              termEnd: formattedTermEnd,
            })
            : t('stripeUpgrade.confirm.description', { planName: t(planDisplay.packageName) })}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.sm,
            border: `1px solid ${palette.elevation.outlined}`,
            borderRadius: borderRadius.md,
            padding: spaces.md,
            backgroundColor: palette.common.white,
          }}
          data-testid='stripe-upgrade-confirm-breakdown'
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body2' color={palette.text.secondary}>
              {t('stripeUpgrade.confirm.cadenceLabel')}
            </Typography>
            <Typography variant='body2' color={palette.text.primary}>
              {cadenceLabel}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body2' color={palette.text.secondary}>
              {t('stripeUpgrade.confirm.currentMonthlyLabel')}
            </Typography>
            <Typography variant='body2' color={palette.text.primary}>
              {formatEuro(currentMonthlyPrice)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body2' color={palette.text.secondary}>
              {t('stripeUpgrade.confirm.newMonthlyLabel')}
            </Typography>
            <Typography variant='body2' color={palette.text.primary}>
              {formatEuro(newMonthlyPrice)}
            </Typography>
          </Box>
          {discount && discount.totalAmount > 0 && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              data-testid='stripe-upgrade-confirm-discount'
            >
              <Typography variant='body2' color={palette.success.main}>
                {discount.name
                  ? t('stripeUpgrade.confirm.discountLabelWithName', { name: discount.name })
                  : t('stripeUpgrade.confirm.discountLabel')}
              </Typography>
              <Typography variant='body2' color={palette.success.main}>
                {`-${formatEuro(discount.totalAmount)}`}
              </Typography>
            </Box>
          )}
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body1' color={palette.text.primary} sx={{ fontWeight: 600 }}>
              {t('stripeUpgrade.confirm.dueTodayLabel')}
            </Typography>
            <Typography
              variant='body1'
              color={palette.text.primary}
              sx={{ fontWeight: 600 }}
              data-testid='stripe-upgrade-confirm-proration'
            >
              {formatEuro(prorationAmount)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const formatPaymentMethodSummary = (): string => {
    if (!preview?.paymentMethod) return t('stripeUpgrade.confirm.paymentMethodGenericSummary');
    const pm = preview.paymentMethod;
    if (pm.type === 'sepa_debit' && pm.last4) {
      return t('stripeUpgrade.confirm.paymentMethodSepa', { last4: pm.last4 });
    }
    if (pm.type === 'card' && pm.last4) {
      const brandLabel = pm.brand
        ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
        : t('stripeUpgrade.confirm.paymentMethodCardGeneric');
      return t('stripeUpgrade.confirm.paymentMethodCard', { brand: brandLabel, last4: pm.last4 });
    }
    if (pm.type === 'paypal') {
      return t('stripeUpgrade.confirm.paymentMethodPaypal');
    }
    if (pm.type === 'us_bank_account') {
      return pm.last4
        ? t('stripeUpgrade.confirm.paymentMethodBankAccount', { last4: pm.last4 })
        : t('stripeUpgrade.confirm.paymentMethodBankAccountGeneric');
    }
    // Any other Stripe payment-method type — fall back to the localized
    // generic label rather than echoing the raw enum (e.g. `sofort`,
    // `bancontact`) into the UI untranslated.
    return t('stripeUpgrade.confirm.paymentMethodGenericSummary');
  };

  // Pick a representative MUI icon for the method type. SEPA (and other bank
  // debits) → AccountBalance; everything else (card / paypal / generic) →
  // CreditCard. Kept deliberately small — brand-specific logos (Visa /
  // Mastercard / PayPal) would need SVG assets and a wider redesign.
  const renderPaymentMethodIcon = (): React.ReactNode => {
    const type = preview?.paymentMethod?.type;
    if (type === 'sepa_debit' || type === 'us_bank_account') {
      return <AccountBalanceIcon fontSize='medium' />;
    }
    return <CreditCardIcon fontSize='medium' />;
  };

  // Yearly shows the full-year price (monthly ×12). No separate "bis" date:
  // the SDK has no subscription-end field and yearly term end == next payment.
  const renderConfirmSummary = (): React.ReactNode => {
    if (!preview) return null;
    const { prorationAmount, newMonthlyPrice, cadence, periodEnd } = preview;
    const isYearly = cadence === 'yearly';
    const recurringAmount = formatEuro(isYearly ? newMonthlyPrice * 12 : newMonthlyPrice);
    const nextPaymentDate = periodEnd ? formatPeriodEnd(periodEnd) : null;

    if (!nextPaymentDate) return null;

    return (
      <Typography
        variant='body2'
        color={palette.text.secondary}
        data-testid='stripe-upgrade-confirm-summary'
      >
        {t(isYearly ? 'stripeUpgrade.confirm.summaryYearly' : 'stripeUpgrade.confirm.summaryMonthly', {
          today: formatEuro(prorationAmount),
          newAmount: recurringAmount,
          nextDate: nextPaymentDate,
        })}
      </Typography>
    );
  };

  const renderRightColumn = () => {
    const isReady = preview !== null && targetPlanKey !== null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.lg,
          maxWidth: 480,
          marginX: 'auto',
          paddingY: spaces.xxl,
        }}
      >
        <Box>
          <Typography variant='h6' color={palette.text.primary} sx={{ fontWeight: 600 }}>
            {t('stripeUpgrade.confirm.paymentMethodHeading')}
          </Typography>
          <Typography variant='body2' color={palette.text.secondary} sx={{ mt: spaces.xs }}>
            <Trans
              i18nKey='stripeUpgrade.confirm.paymentMethodDescription'
              components={[
                <AccountLink
                  key='account-link'
                  to={`/${routes.personalDetails.root}/${ACCOUNT_SECTION}`}
                />,
              ]}
            />
          </Typography>
        </Box>
        <Box
          data-testid='stripe-upgrade-confirm-payment-method'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spaces.md,
            padding: spaces.md,
            border: `1px solid ${palette.elevation.outlined}`,
            borderRadius: borderRadius.md,
            backgroundColor: palette.common.white,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: borderRadius.sm,
              backgroundColor: palette.primary.selected,
              color: palette.primary.main,
              flexShrink: 0,
            }}
          >
            {renderPaymentMethodIcon()}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.xxs, flex: 1, minWidth: 0 }}>
            <Typography variant='caption' color={palette.text.secondary}>
              {t('stripeUpgrade.confirm.paymentMethodSavedLabel')}
            </Typography>
            <Typography
              variant='body1'
              color={palette.text.primary}
              sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              data-testid='stripe-upgrade-confirm-payment-method-summary'
            >
              {formatPaymentMethodSummary()}
            </Typography>
          </Box>
        </Box>
        {didSucceed && (
          <Alert severity='success' data-testid='stripe-upgrade-confirm-success'>
            {t('stripeUpgrade.upgradeSuccess')}
          </Alert>
        )}
        {submitErrorKey && (
          <Alert severity='error' data-testid='stripe-upgrade-confirm-error'>
            {t(submitErrorKey)}
          </Alert>
        )}
        <Button
          variant='contained'
          color='primary'
          size='large'
          fullWidth
          disabled={!isReady || isSubmitting || didSucceed}
          onClick={handleConfirm}
          data-testid='stripe-upgrade-confirm-submit'
        >
          {t('stripeUpgrade.confirm.submitButton')}
        </Button>
        {isReady && renderConfirmSummary()}
      </Box>
    );
  };

  return (
    <Box
      data-testid='stripe-upgrade-confirm-page'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <AppNavBar title={t('stripeUpgrade.confirm.pageTitle')} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktopScreen ? 'row' : 'column',
          flex: 1,
        }}
      >
        <Box
          sx={{
            flex: 1,
            backgroundColor: palette.background.paperElevation0,
            paddingY: spaces.xxxl,
            paddingX: spaces.lg,
          }}
        >
          {renderLeftColumn()}
        </Box>
        <Box
          sx={{
            flex: 1,
            backgroundColor: palette.background.paperElevation2,
            paddingY: spaces.xxxl,
            paddingX: spaces.lg,
          }}
        >
          {renderRightColumn()}
        </Box>
      </Box>
    </Box>
  );
};

export default StripeUpgradeConfirmPage;
